import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { 
    createSpace,
    validateSpaceName,
    generateUsernameFromSpaceName,
    updateSpaceDesignSettings
} from '../utils/spaceHelpers';
import { 
    validateUsername, 
    checkSpacenameAvailability 
} from '../utils/usernameHelpers';
import { getDatabase, ref as dbRef, set, get, remove } from 'firebase/database';
import './SpaceCreationWizard.css';
import SimpleColorPaletteGenerator from './components/SimpleColorPaletteGenerator';

/**
 * Enhanced Space Creation Wizard - One Pager Style
 * Erstellt Space + erste Page mit erweiterten Design-Optionen
 */
const SpaceCreationWizard = ({ onComplete, onCancel }) => {
    const [creating, setCreating] = useState(false);
    const [spaceData, setSpaceData] = useState({
        // Basic Info
        spaceName: '',
        username: '',
        contextInfo: '',
        
        colors: {
            color1: '#CDD1C4',
            color2: '#5C80BC', 
            color3: '#EBF5DF',
            color4: '#30323D',
            color5: '#FFFFFF'
        },
        
        // Page Info
        firstPageName: 'Home',
        firstPageDescription: ''
    });
    const [usernameValid, setUsernameValid] = useState(null);
    const [colorsSaved, setColorsSaved] = useState(false);
    const [localSavedPalettes, setLocalSavedPalettes] = useState([]); // Store locally saved palettes

    const auth = getAuth();
    const navigate = useNavigate();
    const currentUser = auth.currentUser;
    const db = getDatabase();

    const handleSpaceNameChange = (spaceName) => {
        const generatedUsername = generateUsernameFromSpaceName(spaceName);
        setSpaceData(prev => ({
            ...prev,
            spaceName,
            username: generatedUsername
        }));
        
        // Auto-validate username
        if (generatedUsername.length >= 3) {
            validateUsernameAsync(generatedUsername);
        }
    };

    const handleUsernameChange = (username) => {
        const cleanUsername = username.toLowerCase().replace(/[^a-z0-9-]/g, '');
        setSpaceData(prev => ({
            ...prev,
            username: cleanUsername
        }));
        
        if (cleanUsername.length >= 3) {
            validateUsernameAsync(cleanUsername);
        } else {
            setUsernameValid(null);
        }
    };

    const validateUsernameAsync = async (username) => {
        try {
            const validation = validateUsername(username);
            if (!validation.valid) {
                setUsernameValid({ valid: false, error: validation.error });
                return;
            }

            const isAvailable = await checkSpacenameAvailability(username);
            setUsernameValid({ 
                valid: isAvailable, 
                error: isAvailable ? '' : 'Username already taken' 
            });
        } catch (error) {
            console.error('Username validation error:', error);
            setUsernameValid({ valid: false, error: 'Validation failed' });
        }
    };

    // Transfer locally saved palettes to the new space
    const transferLocalPalettesToSpace = async (spaceId) => {
        if (!currentUser || localSavedPalettes.length === 0) return;
        
        try {
            console.log(`🎨 Transferring ${localSavedPalettes.length} local palettes to space ${spaceId}`);
            
            const spacePalettesRef = dbRef(db, `/users/${currentUser.uid}/spaces/${spaceId}/savedColorPalettes`);
            
            // Convert local palettes to Firebase format
            const spacePalettes = {};
            localSavedPalettes.forEach((palette, index) => {
                const key = `palette_${Date.now()}_${index}`;
                spacePalettes[key] = {
                    name: palette.name,
                    colors: palette.colors,
                    style: palette.style,
                    createdAt: palette.createdAt,
                    spaceId: spaceId
                };
            });
            
            await set(spacePalettesRef, spacePalettes);
            console.log(`✅ Successfully transferred ${Object.keys(spacePalettes).length} palettes to space ${spaceId}`);
            
            // Clear local palettes after successful transfer
            setLocalSavedPalettes([]);
            
        } catch (error) {
            console.error('❌ Error transferring palettes to space:', error);
        }
    };

    const canProceedToDesign = () => {
        return spaceData.spaceName.trim() && 
               spaceData.username.trim() && 
               usernameValid?.valid;
    };

    const handleColorsFromGenerator = (newColors, showFeedback = false) => {
        const colorObj = {
            color1: newColors.color1,
            color2: newColors.color2,
            color3: newColors.color3,
            color4: newColors.color4,
            color5: newColors.color5
        };
        
        setSpaceData(prev => ({ ...prev, colors: colorObj }));
        
        // Nur Feedback zeigen wenn explizit gewünscht (z.B. beim Speichern)
        if (showFeedback) {
            setColorsSaved(true);
            setTimeout(() => setColorsSaved(false), 3000);
        }
    };

    const handleCreateSpace = async () => {
        if (!canProceedToDesign()) {
            alert('Please fill in all required fields');
            return;
        }

        setCreating(true);

        try {
            // Create space with design settings
            const designSettings = {
                contextInfo: spaceData.contextInfo,
                colors: spaceData.colors
            };
            
            const spaceResult = await createSpace(
                currentUser.uid,
                spaceData.spaceName,
                spaceData.username,
                designSettings
            );

            console.log('✅ Space created successfully:', spaceResult);
            
            if (!spaceResult || !spaceResult.spaceId) {
                throw new Error('Invalid space creation result');
            }

            // Create first page using correct path structure
            const firstPageRef = dbRef(db, `/pages/${currentUser.uid}/${spaceData.username}/home`);
            const homePageData = {
                name: spaceData.firstPageName || 'Home',
                description: spaceData.firstPageDescription || 'Welcome to my space',
                slug: 'home',
                userId: currentUser.uid,
                spaceId: spaceData.username,
                username: spaceData.username,
                isHomePage: true,
                isActive: true,
                viewCounter: 0,
                widgets: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                
                // Inherit design settings from space
                backgroundColor: spaceData.colors?.color5 || '#FFFFFF',
                colors: spaceData.colors || {
                    color1: '#CDD1C4',
                    color2: '#5C80BC',
                    color3: '#EBF5DF',
                    color4: '#30323D',
                    color5: '#FFFFFF'
                }
            };
            
            console.log('📄 Creating home page with data:', homePageData);
            await set(firstPageRef, homePageData);

            // Create URL mapping for home page
            const urlMappingRef = dbRef(db, `/urlMappings/${spaceData.username}/`);
            await set(urlMappingRef, { 
                pageId: `${currentUser.uid}/${spaceData.username}/home`,
                userId: currentUser.uid,
                spaceId: spaceData.username,
                username: spaceData.username,
                isHomePage: true
            });

            console.log('✅ URL mapping created for home page');

            // Transfer any locally saved palettes to the new space
            await transferLocalPalettesToSpace(spaceResult.spaceId);

            // Success - redirect to page settings
            const settingsRoute = `/settings/page/${currentUser.uid}/${spaceData.username}/home`;
            console.log('🔗 Navigating to:', settingsRoute);
            
            // Call onComplete with the created space data
            if (onComplete) {
                const completeSpaceData = {
                    spaceId: spaceData.username, // This is the space ID
                    username: spaceData.username,
                    spaceName: spaceData.name,
                    description: spaceData.description,
                    ...spaceData
                };
                console.log('🚀 SpaceCreationWizard calling onComplete with:', completeSpaceData);
                onComplete(completeSpaceData);
            }
            
            // Small delay to ensure state updates are processed
            setTimeout(() => {
                navigate(settingsRoute);
            }, 100);
        } catch (error) {
            console.error('Space creation error:', error);
            alert('Failed to create space: ' + error.message);
        } finally {
            setCreating(false);
        }
    };

    return (
        <>
            <div 
                className="ai-space-creator-overlay"
                onClick={(e) => {
                    // Close if clicking outside the modal
                    if (e.target === e.currentTarget) {
                        onCancel();
                        document.body.style.overflow = 'auto';
                    }
                }}
            >
                <div className="ai-space-creator">
                    {/* Close Button */}
                    <button 
                        className="ai-space-creator-close-btn"
                        onClick={() => {
                            onCancel();
                            document.body.style.overflow = 'auto';
                        }}
                        title="Close"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                    
                    {/* Header */}
                    <div className="ai-space-creator-header">
                        <div className="ai-space-creator-icon">
                            <i className="fas fa-rocket"></i>
                        </div>
                        <h2>Create SalesPage</h2>
                        <p>Create your perfect digital space in minutes. Just fill out the details below and let AI help you build something amazing.</p>
                    </div>

                    {/* Content - One Pager Style */}
                    <div className="ai-space-creator-content">
                        
                        {/* Basic Information Section */}
                        <div className="ai-space-creator-section">
                            <h3><i className="fas fa-user-circle"></i> Basic Information</h3>
                            <p>Tell us about yourself and your SalesPage. This information will help us create the perfect setup for you.</p>
                            
                            <div className="ai-space-creator-form-section">
                                <div className="ai-space-creator-input-group">
                                    <label className="ai-space-creator-input-label">Project Name</label>
                                    <input
                                        type="text"
                                        value={spaceData.spaceName}
                                        onChange={(e) => handleSpaceNameChange(e.target.value)}
                                        placeholder="e.g., My Awesome Business"
                                        className="ai-space-creator-input-field"
                                    />
                                </div>

                                <div className="ai-space-creator-input-group">
                                    <label className="ai-space-creator-input-label">BonusClub Link (URL)</label>
                                    <div className="ai-space-creator-url-input-wrapper">
                                        <span className="ai-space-creator-url-prefix">bonusclub.link/</span>
                                        <input
                                            type="text"
                                            value={spaceData.username}
                                            onChange={(e) => handleUsernameChange(e.target.value)}
                                            placeholder="your-username"
                                            className="ai-space-creator-url-input"
                                        />
                                    </div>
                                    {usernameValid && (
                                        <div className={`ai-space-creator-input-hint ${usernameValid.valid ? 'success' : 'error'}`}>
                                            {usernameValid.valid ? '✓ Username is available' : usernameValid.error}
                                        </div>
                                    )}
                                </div>

                                <div className="ai-space-creator-input-group">
                                    <label className="ai-space-creator-input-label">Information about your Project</label>
                                    <div className="ai-description-container">
                                        <textarea
                                            value={spaceData.contextInfo}
                                            onChange={(e) => setSpaceData(prev => ({ ...prev, contextInfo: e.target.value }))}
                                            onFocus={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            placeholder="Describe your business, project, or what this space is about. This helps AI create better content for you..."
                                            className="ai-space-creator-textarea"
                                            rows={4}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Design Options Section */}
                        <div className="ai-space-creator-section">
                            <h3><i className="fas fa-palette"></i> Design Options</h3>
                            <p>Customize the colors of your space that represent your brand.</p>
                            
                            <div className="ai-space-creator-form-section">

                                {/* Color Selection */}
                                <div className="ai-space-creator-input-group">
                                    <label className="ai-space-creator-input-label">Color Palette</label>
                                    
                                    {/* Simple Color Palette Generator */}
                                    <SimpleColorPaletteGenerator
                                        currentColors={[
                                            spaceData.colors.color1,
                                            spaceData.colors.color2,
                                            spaceData.colors.color3,
                                            spaceData.colors.color4,
                                            spaceData.colors.color5
                                        ]}
                                        onColorsChange={handleColorsFromGenerator}
                                        onPalettesChange={setLocalSavedPalettes}
                                    />
                                    
                                    {colorsSaved && (
                                        <div className="ai-space-creator-colors-saved-feedback">
                                            <i className="fas fa-check"></i>
                                            New colors applied successfully!
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>

                    </div>

                    {/* Actions */}
                    <div className="ai-space-creator-actions">
                        <button 
                            className="ai-space-creator-btn tertiary"
                            onClick={() => {
                                onCancel();
                                document.body.style.overflow = 'auto';
                            }}
                        >
                            Cancel
                        </button>
                        
                        <button 
                            className={`ai-space-creator-btn primary ${creating ? 'loading' : ''}`}
                            onClick={handleCreateSpace}
                            disabled={creating || !canProceedToDesign()}
                        >
                            <div className="btn-text">
                                {creating ? 'Creating Space...' : 'Create Space'}
                            </div>
                            <div className="btn-icon-container">
                                <i className={creating ? "fas fa-spinner fa-spin" : "fas fa-rocket"}></i>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

        </>
    );
};

export default SpaceCreationWizard;